import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { MultiSelectItem } from "./types";

export interface UseMultiSelectProps<TItem extends MultiSelectItem> {
  items: TItem[];
  initialSelectedItems?: TItem[];
  queryName?: string;
  isSingleSelect?: boolean;
  onChange?: (items: TItem[]) => void;
}

export const useMultiSelect = <TItem extends MultiSelectItem>({
  items,
  initialSelectedItems,
  queryName = "",
  isSingleSelect = false,
  onChange,
}: UseMultiSelectProps<TItem>) => {
  const [selectedItems, setSelectedItems] = useState<TItem[]>([]);

  useEffect(() => {
    // if initialSelectedItems is not undefined and it's not equal to selectedItems, then set the selected items to the initialSelectedItems
    if (
      initialSelectedItems &&
      !initialSelectedItems.every((item) => selectedItems.some((selectedItem) => selectedItem.id === item.id))
    ) {
      setSelectedItems(initialSelectedItems);
    }
  }, [initialSelectedItems, selectedItems]);

  const onItemSelect = useCallback(
    (item: TItem) => {
      setSelectedItems((prevState) => {
        if (prevState.some((prevItem) => prevItem.id === item.id)) {
          const newItems = prevState.filter((prevItem) => prevItem.id !== item.id);
          if (onChange) onChange(newItems);
          return newItems;
        } else if (isSingleSelect) {
          const newItems = [item];
          if (onChange) onChange(newItems);
          return newItems;
        } else {
          const newItems = [...prevState, item];
          if (onChange) onChange(newItems);
          return newItems;
        }
      });
    },
    [isSingleSelect, onChange]
  );

  const onRemove = (_item: TItem, index: number) => {
    setSelectedItems((prevState) => {
      const newItems = [...prevState];
      newItems.splice(index, 1);
      if (onChange) onChange(newItems);
      return newItems;
    });
  };

  const onClear = () => {
    setSelectedItems([]);
  };

  const isItemSelected = (item: TItem) => selectedItems.some((selectedItem) => selectedItem.id === item.id);

  const [searchParams] = useSearchParams();
  const searchParamsValue = searchParams.get(queryName);

  useEffect(() => {
    if (searchParamsValue) {
      const item = items.find((item) => item.label === searchParamsValue);
      if (item) onItemSelect(item);
    }
  }, [searchParamsValue, items, onItemSelect]);

  return { items, selectedItems, isSingleSelect, onItemSelect, onRemove, onClear, isItemSelected };
};

export type UseMultiSelect<TItem extends MultiSelectItem> = ReturnType<typeof useMultiSelect<TItem>>;
